/* global google */

import React, { useEffect, useRef, useMemo } from 'react';

// Track if Google Maps script is already loaded
let isGoogleMapsScriptLoaded = false;
let googleMapsCallbackQueue = [];

// Function to load Google Maps script once
const loadGoogleMaps = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    if (isGoogleMapsScriptLoaded) {
      // Wait for existing load
      googleMapsCallbackQueue.push(resolve);
      return;
    }

    isGoogleMapsScriptLoaded = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      resolve();
      // Resolve all waiting callbacks
      googleMapsCallbackQueue.forEach(cb => cb());
      googleMapsCallbackQueue = [];
    };

    script.onerror = () => {
      const error = new Error('Google Maps script failed to load');
      reject(error);
      googleMapsCallbackQueue.forEach(cb => cb(error));
      googleMapsCallbackQueue = [];
    };

    document.head.appendChild(script);
  });
};

const MapContainer = ({
  origin,
  destination,
  waypoints = [],
  height = '400px',
  showRoute = false,
  center = { lat: 12.9716, lng: 77.5946 },
  zoom = 12,
  onMapClick,
  markers = [] // Array of custom markers: [{ position: {lat, lng}, title, icon }]
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const directionsRenderer = useRef(null);
  const customMarkersRef = useRef([]);
  const markerOriginRef = useRef(null);
  const markerDestinationRef = useRef(null);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // Debug: Log environment variable status
    console.log('=== Google Maps API Key Debug ===');
    console.log('API Key present:', !!apiKey);
    console.log('API Key value:', apiKey ? `${apiKey.substring(0, 10)}...` : 'NOT SET');
    console.log('All REACT_APP env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));
    console.log('===============================');
    
    if (!apiKey) {
      console.error('REACT_APP_GOOGLE_MAPS_API_KEY is not set');
      console.error('Troubleshooting steps:');
      console.error('1. Check that client/.env file exists');
      console.error('2. Verify file contains: REACT_APP_GOOGLE_MAPS_API_KEY=your_key');
      console.error('3. RESTART your React dev server (npm start)');
      console.error('4. React only reads .env files at startup, not during runtime');
      if (mapRef.current) {
        mapRef.current.innerHTML = '<div class="text-red-500 text-center p-4 font-bold">⚠️ Google Maps API key is missing.<br/>Please check the browser console for details.<br/>Make sure to RESTART your React server after adding the .env file.</div>';
      }
      return;
    }

    if (!mapRef.current) return;

    let map;

    const initializeMap = () => {
      // Clean up previous map elements
      if (mapInstance.current) {
        // Don't recreate map if it exists, just update markers
        map = mapInstance.current;
      } else {
        map = new google.maps.Map(mapRef.current, {
          center: origin || destination || center,
          zoom: zoom,
          mapTypeId: google.maps.MapTypeId.ROADMAP
        });
        mapInstance.current = map;

        // Add click listener (only once)
        if (onMapClick) {
          map.addListener('click', onMapClick);
        }
      }

      // Update origin marker
      if (origin) {
        if (markerOriginRef.current) {
          markerOriginRef.current.setPosition(origin);
        } else {
          markerOriginRef.current = new google.maps.Marker({
            position: origin,
            map,
            title: 'Origin',
            icon: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
          });
        }
      } else if (markerOriginRef.current) {
        markerOriginRef.current.setMap(null);
        markerOriginRef.current = null;
      }

      // Update destination marker
      if (destination) {
        if (markerDestinationRef.current) {
          markerDestinationRef.current.setPosition(destination);
        } else {
          markerDestinationRef.current = new google.maps.Marker({
            position: destination,
            map,
            title: 'Destination',
            icon: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
          });
        }
      } else if (markerDestinationRef.current) {
        markerDestinationRef.current.setMap(null);
        markerDestinationRef.current = null;
      }

      // Update custom markers (e.g., rider location with bike icon)
      // Remove old custom markers
      customMarkersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      customMarkersRef.current = [];

      // Add new custom markers
      if (markers && Array.isArray(markers) && markers.length > 0) {
        markers.forEach(markerData => {
          if (markerData.position) {
            const markerOptions = {
              position: markerData.position,
              map,
              title: markerData.title || 'Marker'
            };
            
            // Handle icon - can be string URL or icon object
            if (markerData.icon) {
              if (typeof markerData.icon === 'string') {
                markerOptions.icon = markerData.icon;
              } else if (typeof markerData.icon === 'object') {
                // Icon object with url, scaledSize, anchor
                markerOptions.icon = {
                  url: markerData.icon.url,
                  scaledSize: markerData.icon.scaledSize 
                    ? new google.maps.Size(markerData.icon.scaledSize.width || 40, markerData.icon.scaledSize.height || 40)
                    : new google.maps.Size(40, 40),
                  anchor: markerData.icon.anchor
                    ? new google.maps.Point(markerData.icon.anchor.x || 20, markerData.icon.anchor.y || 20)
                    : new google.maps.Point(20, 20)
                };
              }
            }
            
            const customMarker = new google.maps.Marker(markerOptions);
            customMarkersRef.current.push(customMarker);
          }
        });
      }

      // Render route
      if (showRoute && origin && destination) {
        const directionsService = new google.maps.DirectionsService();
        directionsRenderer.current = new google.maps.DirectionsRenderer({
          suppressMarkers: false,
          preserveViewport: false
        });
        directionsRenderer.current.setMap(map);

        // Prepare waypoints for Directions API
        const waypointsParam = waypoints && waypoints.length > 0
          ? waypoints
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(wp => ({
                location: new google.maps.LatLng(wp.coordinates.lat, wp.coordinates.lng),
                stopover: wp.stopover !== false
              }))
          : null;

        const request = {
          origin: origin,
          destination: destination,
          travelMode: google.maps.TravelMode.DRIVING
        };

        if (waypointsParam && waypointsParam.length > 0) {
          request.waypoints = waypointsParam;
          request.optimizeWaypoints = false; // Keep waypoints in order
        }

        directionsService.route(
          request,
          (result, status) => {
            if (status === 'OK') {
              directionsRenderer.current.setDirections(result);
              // Fit map to show entire route
              const bounds = new google.maps.LatLngBounds();
              result.routes[0].overview_path.forEach(path => {
                bounds.extend(path);
              });
              // Also include waypoints in bounds
              if (waypoints && waypoints.length > 0) {
                waypoints.forEach(wp => {
                  bounds.extend(new google.maps.LatLng(wp.coordinates.lat, wp.coordinates.lng));
                });
              }
              map.fitBounds(bounds);
            } else {
              console.warn('Directions request failed:', status);
              // Fallback: show markers with bounds
              if (origin && destination) {
                const bounds = new google.maps.LatLngBounds();
                bounds.extend(origin);
                bounds.extend(destination);
                if (waypoints && waypoints.length > 0) {
                  waypoints.forEach(wp => {
                    bounds.extend(new google.maps.LatLng(wp.coordinates.lat, wp.coordinates.lng));
                  });
                }
                map.fitBounds(bounds);
              }
            }
          }
        );
      } else if (origin && destination) {
        // Fit bounds to show both markers if route not requested
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(origin);
        bounds.extend(destination);
        map.fitBounds(bounds);
      } else if (origin) {
        map.setCenter(origin);
        map.setZoom(15);
      } else if (destination) {
        map.setCenter(destination);
        map.setZoom(15);
      } else if (center) {
        // Update center if provided and no origin/destination
        map.setCenter(center);
        if (zoom) {
          map.setZoom(zoom);
        }
      }
      
      // Smoothly pan to rider location when it updates (for passengers tracking rider)
      if (markers && Array.isArray(markers) && markers.length > 0) {
        const riderMarker = markers.find(m => 
          m.title?.includes('Rider') || 
          m.title?.includes('Your Location') && !m.title?.includes('blue-dot')
        );
        if (riderMarker && riderMarker.position) {
          // Smoothly pan to rider location (only if map already exists)
          if (mapInstance.current) {
            map.panTo(riderMarker.position);
          }
        }
      }
    };

    loadGoogleMaps(apiKey)
      .then(() => {
        if (mapRef.current) {
          initializeMap();
        }
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error);
        if (mapRef.current) {
          mapRef.current.innerHTML = '<div class="text-red-500 text-center p-4">Failed to load map. Check API key and network connection.</div>';
        }
      });

    // Cleanup
    return () => {
      if (markerOriginRef.current) markerOriginRef.current.setMap(null);
      if (markerDestinationRef.current) markerDestinationRef.current.setMap(null);
      customMarkersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      customMarkersRef.current = [];
      if (directionsRenderer.current) {
        directionsRenderer.current.setMap(null);
      }
    };
  }, [origin, destination, waypoints, showRoute, center, zoom, onMapClick, markers]);

  return (
    <div
      ref={mapRef}
      style={{ height, width: '100%' }}
      className="rounded-lg overflow-hidden border border-gray-200"
    />
  );
};

export default MapContainer;